import { ArticlesFeedPage, FeedArticle } from '../types';
import { FeedSortBy, FeedSortDir } from '../lib/feedRequest';
import { FeedSocketDisconnectError } from '../lib/feedDisconnectError';

// COM-638 task 4: pure state-transition logic for AllArticlesPage. Extracted
// out of the component so the append-vs-replace decision - the one place
// off-by-ones live in an endless-scroll feed - can be unit-tested directly
// (see allArticlesFeed.test.ts) instead of only being exercised indirectly
// through the UI.

/** Why a fetch was issued - decides what FETCH_SUCCESS does with the reply:
 *  - 'initial': first mount. Replaces (an empty list).
 *  - 'reset': search/sort changed. Replaces the list and restarts from offset 0.
 *  - 'append': endless-scroll sentinel fired. Appends to the existing list. */
export type FeedFetchKind = 'initial' | 'reset' | 'append';

export type FeedStatus = 'loading-initial' | 'loading-more' | 'refreshing' | 'ready' | 'error' | 'disconnected';

export interface FeedState {
  articles: FeedArticle[];
  total: number;
  status: FeedStatus;
  errorMessage: string | null;
  // Fix wave (review round 3): consecutive `fetchError` results for an
  // `append` fetch, with no successful fetch in between. Only append errors
  // count - see `canFetchNextPage`/`MAX_CONSECUTIVE_APPEND_ERRORS` below for
  // why.
  consecutiveAppendErrors: number;
  // COM-640: the project-wide "last updated" aggregate carried on every
  // `ArticlesFeedPage` reply (types.ts) - stored here (not read straight off
  // the last reply by the page) so ScanIndicator has something to render
  // even between fetches. Persists across every action except `fetchSuccess`
  // itself (every other case spreads `...state`), including a disconnect/
  // error - a failed refetch must not blank out an otherwise-valid label.
  lastUpdatedAt: string | null;
}

export type FeedAction =
  | { type: 'fetchStart'; kind: FeedFetchKind }
  | { type: 'fetchSuccess'; kind: FeedFetchKind; page: ArticlesFeedPage }
  | { type: 'fetchDisconnected'; kind: FeedFetchKind }
  | { type: 'fetchError'; kind: FeedFetchKind; message: string }
  // COM-638 fix: optimistic local counterpart to
  // SourcesContext#markArticleRead's fire-and-forget `updateArticleStatus`
  // emit - clears the matching article's `isNew` immediately so the badge
  // disappears without waiting on a round trip. A daily refetch reconciles
  // anything this missed (e.g. the emit itself failing silently).
  | { type: 'markRead'; url: string };

export const initialFeedState: FeedState = {
  articles: [],
  total: 0,
  status: 'loading-initial',
  errorMessage: null,
  consecutiveAppendErrors: 0,
  lastUpdatedAt: null,
};

function statusForStart(kind: FeedFetchKind): FeedStatus {
  if (kind === 'initial') return 'loading-initial';
  if (kind === 'append') return 'loading-more';
  return 'refreshing';
}

// Fix wave (review round 3): how many consecutive `append` fetches may fail
// (with no successful fetch in between) before the reducer stops returning
// to a retryable 'ready' status and pins 'error' instead. Bounds the
// auto-retry loop described on `canFetchNextPage` below - without a cap, a
// PERSISTENT server error would retry this many times on every single
// render that leaves the endless-scroll sentinel on screen, which for a
// server that is reliably failing is effectively unbounded. 3 is small
// enough to fail fast, large enough not to trip on one flaky reply.
export const MAX_CONSECUTIVE_APPEND_ERRORS = 3;

export function feedReducer(state: FeedState, action: FeedAction): FeedState {
  switch (action.type) {
    case 'fetchStart':
      return { ...state, status: statusForStart(action.kind), errorMessage: null };

    case 'fetchSuccess': {
      const articles =
        action.kind === 'append' ? [...state.articles, ...action.page.articles] : action.page.articles;
      return {
        articles,
        total: action.page.total,
        status: 'ready',
        errorMessage: null,
        consecutiveAppendErrors: 0,
        lastUpdatedAt: action.page.lastUpdatedAt,
      };
    }

    case 'fetchDisconnected':
      return { ...state, status: 'disconnected' };

    case 'fetchError': {
      // Initial/reset failures always pin 'error' - AllArticlesPage renders
      // an inline error state with a Retry button for this status (see
      // AllArticlesPage.tsx's renderBody), and nothing re-fires an initial/
      // reset load automatically, so there's no loop to guard here.
      if (action.kind !== 'append') {
        return { ...state, status: 'error', errorMessage: action.message, consecutiveAppendErrors: 0 };
      }

      // Append failures are different: AllArticlesPage's endless-scroll
      // sentinel re-attaches its IntersectionObserver on every state change
      // (its callback ref depends on `onEndReached`, which depends on
      // `state`) and a freshly-observed element reports its CURRENT
      // intersection state immediately - so if a failed append just
      // returned to a settled, retryable status while the sentinel is still
      // on screen (likely: a failed append never grows the page, so
      // nothing about the sentinel's position changed), that reattach fires
      // `onEndReached` again with no user action at all. A single persistent
      // server error would otherwise retry every time this happens - see
      // `MAX_CONSECUTIVE_APPEND_ERRORS` above for why that's bounded rather
      // than removed outright: below the cap, the status goes back to
      // 'ready' so scrolling (or that automatic reattach) CAN retry; once
      // the cap is hit, status pins to 'error' - `canFetchNextPage` refuses
      // to fire again until the user hits the page's explicit Retry
      // control, which resets the streak via the next `fetchSuccess`.
      const consecutiveAppendErrors = state.consecutiveAppendErrors + 1;
      const exhausted = consecutiveAppendErrors >= MAX_CONSECUTIVE_APPEND_ERRORS;
      return {
        ...state,
        status: exhausted ? 'error' : 'ready',
        errorMessage: exhausted ? action.message : null,
        consecutiveAppendErrors,
      };
    }

    case 'markRead': {
      const index = state.articles.findIndex((a) => a.url === action.url);
      // No-op, same reference, for both "unknown url" (index === -1) and
      // "already read" - a matching article that's already !isNew has
      // nothing to change, and returning the same state avoids a pointless
      // re-render on every markRead dispatch for an article that isn't new.
      if (index === -1 || !state.articles[index].isNew) {
        return state;
      }
      const articles = state.articles.slice();
      articles[index] = { ...articles[index], isNew: false };
      return { ...state, articles };
    }

    default:
      return state;
  }
}

/** True while `state.status` reflects a fetch that hasn't settled yet. */
export function isFetchInFlight(status: FeedStatus): boolean {
  return status === 'loading-initial' || status === 'loading-more' || status === 'refreshing';
}

/** Endless-scroll guard: only fetch the next page when nothing is already in
 * flight, the feed isn't pinned in an error state, and there are more rows
 * on the server than are currently loaded.
 *  - The `articles.length < total` half is the off-by-one this exists to
 *    catch - without it, a sentinel that's still on-screen after the last
 *    page loads would fire one request too many.
 *  - The `status !== 'error'` half (review round 3) is the loop guard: an
 *    exhausted append-error streak (see `MAX_CONSECUTIVE_APPEND_ERRORS` in
 *    `feedReducer`) pins `status: 'error'` specifically so THIS check stops
 *    the endless-scroll sentinel's automatic re-observe/retry (see the
 *    comment on `feedReducer`'s `fetchError` case) from firing again -
 *    without it, a persistent server error would keep retrying forever
 *    every time the sentinel's intersection observer re-attaches. */
export function canFetchNextPage(state: FeedState): boolean {
  if (state.status === 'error') return false;
  return !isFetchInFlight(state.status) && state.articles.length < state.total;
}

/** Publication date formatting policy for the feed's cards: DD.MM.YYYY,
 * parsed as UTC (matches how the backend stores/serves it) so this doesn't
 * drift a day depending on the viewer's timezone. `updateDate` is nullable
 * on `FeedArticle` (COM-638) - both that and an unparsable string fall back
 * to an em dash rather than "Invalid Date". */
export function formatFeedArticleDate(updateDate: string | null): string {
  if (!updateDate) return '—';
  const parsed = new Date(updateDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  const dd = String(parsed.getUTCDate()).padStart(2, '0');
  const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = parsed.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

/** Distinguishes `fetchArticlesFeed`'s disconnect/reconnect rejection (see
 * SourcesContext.tsx's `rejectPendingArticlesFeedRequests`) from any other
 * rejection, so the page can show a "Connection lost" + Retry affordance for
 * the former and fall back to the ordinary toast/inline-error handling for
 * anything else.
 *
 * Review round 3: this used to be a message regex
 * (`/disconnect|reconnect/i`), which happened to work only because
 * disconnect/reconnect were the ONLY rejections `fetchArticlesFeed` could
 * ever produce at the time - every rejection matched, so the 'error' status
 * this function gates was unreachable. Now that a server-side `error: true`
 * reply and the client-side timeout (both in SourcesContext.tsx) can reject
 * the same promise for unrelated reasons, telling them apart has to be
 * structural rather than wording-based - see feedDisconnectError.ts. */
export function isFeedDisconnectError(error: unknown): boolean {
  return error instanceof FeedSocketDisconnectError;
}

export interface FeedSortOption {
  sortBy: FeedSortBy;
  sortDir: FeedSortDir;
  label: string;
}

// Figma 2871:49860's sort menu order - "Newest first" is the feed's own
// default (see feedRequest.ts's DEFAULT_SORT_BY/DEFAULT_SORT_DIR), so it
// must stay first/index 0.
export const SORT_OPTIONS: FeedSortOption[] = [
  { sortBy: 'date', sortDir: 'desc', label: 'Newest first' },
  { sortBy: 'date', sortDir: 'asc', label: 'Oldest first' },
  { sortBy: 'title', sortDir: 'asc', label: 'A–Z' },
  { sortBy: 'title', sortDir: 'desc', label: 'Z–A' },
];

export const DEFAULT_SORT_OPTION = SORT_OPTIONS[0];

export function findSortOption(sortBy: FeedSortBy, sortDir: FeedSortDir): FeedSortOption | undefined {
  return SORT_OPTIONS.find((option) => option.sortBy === sortBy && option.sortDir === sortDir);
}
